/**
 * Simple least-squares line y = a + b·x with the usual standard errors, for the
 * Bland–Altman proportional-bias check. Matches `lm(y ~ x)` in R.
 */

import { studentTQuantile, studentTTwoTailed } from "@/lib/stats/distributions";

export interface LinearFit {
  n: number;
  intercept: number;
  slope: number;
  slopeSe: number;
  slopeT: number;
  slopeP: number;
  slopeLower: number;
  slopeUpper: number;
  interceptSe: number;
  rSquared: number;
  /** Residual standard error. */
  sigma: number;
}

/** Null when there are fewer than three points or x has no spread. */
export function linearFit(
  x: readonly number[],
  y: readonly number[],
  level = 0.95
): LinearFit | null {
  const n = Math.min(x.length, y.length);
  if (n < 3) {
    return null;
  }
  const mx = x.reduce((s, v) => s + v, 0) / n;
  const my = y.reduce((s, v) => s + v, 0) / n;
  let sxx = 0;
  let sxy = 0;
  let syy = 0;
  for (let i = 0; i < n; i++) {
    sxx += (x[i] - mx) ** 2;
    sxy += (x[i] - mx) * (y[i] - my);
    syy += (y[i] - my) ** 2;
  }
  if (!(sxx > 0)) {
    return null;
  }
  const slope = sxy / sxx;
  const intercept = my - slope * mx;
  const sse = Math.max(0, syy - slope * sxy);
  const df = n - 2;
  const sigma = Math.sqrt(sse / df);
  const slopeSe = sigma / Math.sqrt(sxx);
  const interceptSe = sigma * Math.sqrt(1 / n + (mx * mx) / sxx);
  const slopeT =
    slopeSe > 0 ? slope / slopeSe : Math.sign(slope) * Number.POSITIVE_INFINITY;
  const critical = studentTQuantile(1 - (1 - level) / 2, df);
  return {
    n,
    intercept,
    slope,
    slopeSe,
    slopeT,
    slopeP: slopeSe > 0 ? studentTTwoTailed(slopeT, df) : 0,
    slopeLower: slope - critical * slopeSe,
    slopeUpper: slope + critical * slopeSe,
    interceptSe,
    rSquared: syy > 0 ? 1 - sse / syy : Number.NaN,
    sigma,
  };
}
